import React, { useState, useEffect } from 'react'
import api from '../services/api.ts'
import { useAuth } from '../context/AuthContext.tsx'
import AccessDenied from '../components/AccessDenied.tsx'
import MessageBanner from '../components/MessageBanner.tsx'
import InvitedUserConfirmation from '../components/InvitedUserConfirmation.tsx'

interface Agency {
  id: string
  name: string
}

interface User {
  id: string
  name: string
  email: string
  is_system_admin: boolean
  agency_users: Array<{
    agencies: {
      id: string
      name: string
    }
  }>
}

interface InvitedUser {
  id: string
  email: string
  name: string
  tempPassword?: string
}

const AdminAgencyUserSignup: React.FC = () => {
  const { user } = useAuth()
  const [agencies, setAgencies] = useState<Agency[]>([])
  const [existingUsers, setExistingUsers] = useState<User[]>([])
  const [mode, setMode] = useState<'existing' | 'new'>('existing')
  const [formData, setFormData] = useState({
    userId: '',
    email: '',
    name: '',
    agencyId: '',
    role: 'MEMBER',
  })
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [invitedUser, setInvitedUser] = useState<InvitedUser | null>(null)

  useEffect(() => {
    fetchAgencies()
    fetchExistingUsers()
  }, [])

  const fetchAgencies = async () => {
    try {
      const response = await api.get('/api/admin/agencies')
      setAgencies(response.data.agencies)
    } catch (error) {
      console.error('Error fetching agencies:', error)
      setError('Failed to load agencies')
    }
  }

  const fetchExistingUsers = async () => {
    try {
      const response = await api.get('/api/admin/users')
      setExistingUsers(response.data.users)
    } catch (error) {
      console.error('Error fetching users:', error)
      setError('Failed to load existing users')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setMessage('')
    setError('')
    setInvitedUser(null)

    try {
      const payload =
        mode === 'existing'
          ? { userId: formData.userId, agencyId: formData.agencyId, role: formData.role }
          : {
              email: formData.email,
              name: formData.name,
              agencyId: formData.agencyId,
              role: formData.role,
            }

      const response = await api.post('/api/admin/agency-users', payload)

      setInvitedUser(response.data.user)
      setMessage(response.data.message)

      // Reset form
      setFormData({
        userId: '',
        email: '',
        name: '',
        agencyId: '',
        role: 'MEMBER',
      })
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to invite user')
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleModeChange = (newMode: 'existing' | 'new') => {
    setMode(newMode)
    setFormData({
      userId: '',
      email: '',
      name: '',
      agencyId: '',
      role: 'MEMBER',
    })
    setMessage('')
    setError('')
    setInvitedUser(null)
  }

  const getSelectedUser = () => {
    return existingUsers.find(u => u.id === formData.userId)
  }

  const selectedUser = getSelectedUser()

  if (!user?.is_system_admin) {
    return <AccessDenied />
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">Invite Agency User</h1>

          <MessageBanner message={message} error={error} />

          {invitedUser && <InvitedUserConfirmation invitedUser={invitedUser} />}

          {/* Mode Toggle */}
          <div className="mb-6">
            <div className="flex space-x-4">
              <button
                type="button"
                onClick={() => handleModeChange('existing')}
                className={`px-4 py-2 rounded-md font-medium ${
                  mode === 'existing'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Select Existing User
              </button>
              <button
                type="button"
                onClick={() => handleModeChange('new')}
                className={`px-4 py-2 rounded-md font-medium ${
                  mode === 'new'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Create New User
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {mode === 'existing' ? (
                <div>
                  <label htmlFor="userId" className="block text-sm font-medium text-gray-700 mb-2">
                    Select User *
                  </label>
                  <select
                    id="userId"
                    name="userId"
                    value={formData.userId}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select a user</option>
                    {existingUsers.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.name} ({user.email}){user.is_system_admin ? ' - System Admin' : ''}
                        {user.agency_users.length > 0
                          ? ` - Already in: ${user.agency_users.map(au => au.agencies.name).join(', ')}`
                          : ''}
                      </option>
                    ))}
                  </select>
                  {selectedUser && (
                    <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded-md">
                      <p className="text-sm text-gray-600">
                        <strong>Selected:</strong> {selectedUser.name} ({selectedUser.email})
                      </p>
                      {selectedUser.agency_users.length > 0 && (
                        <p className="text-sm text-orange-600 mt-1">
                          ⚠️ This user is already associated with:{' '}
                          {selectedUser.agency_users.map(au => au.agencies.name).join(', ')}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter full name"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter email address"
                      required
                    />
                  </div>
                </>
              )}

              <div>
                <label htmlFor="agencyId" className="block text-sm font-medium text-gray-700 mb-2">
                  Agency *
                </label>
                <select
                  id="agencyId"
                  name="agencyId"
                  value={formData.agencyId}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select an agency</option>
                  {agencies.map(agency => (
                    <option key={agency.id} value={agency.id}>
                      {agency.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-2">
                  Role *
                </label>
                <select
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="MEMBER">Member</option>
                  <option value="ADMIN">Admin</option>
                </select>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h3 className="text-yellow-800 font-semibold mb-2">Important Notes:</h3>
              <ul className="text-yellow-700 text-sm space-y-1">
                {mode === 'existing' ? (
                  <>
                    <li>• Selecting an existing user will link them to the selected agency</li>
                    <li>
                      • If the user is already in an agency, they will be added to the new agency as
                      well
                    </li>
                    <li>
                      • System admins can be added to agencies but retain their system admin
                      privileges
                    </li>
                  </>
                ) : (
                  <>
                    <li>
                      • If the email already exists, the user will be linked to the selected agency
                    </li>
                    <li>• If the email is new, a temporary password will be generated</li>
                    <li>• The temporary password should be sent to the user via email</li>
                    <li>• Users should change their password on first login</li>
                  </>
                )}
              </ul>
            </div>

            <button
              type="submit"
              disabled={
                loading ||
                !formData.agencyId ||
                (mode === 'existing' ? !formData.userId : !formData.name || !formData.email)
              }
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading
                ? 'Inviting User...'
                : mode === 'existing'
                  ? 'Link User to Agency'
                  : 'Invite User'}
            </button>
          </form>

          <div className="mt-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Role Descriptions</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-2">Member</h3>
                <p className="text-sm text-gray-600">
                  Can view reports and data for companies their agency has access to. Cannot manage
                  keywords or invite users.
                </p>
              </div>
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-2">Admin</h3>
                <p className="text-sm text-gray-600">
                  Can manage keywords, invite users to their agency, and access all features for
                  companies their agency has access to.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminAgencyUserSignup
